import {
  Card,
  Col,
  Row,
  Tag,
  Button,
  Space,
  Typography,
  Flex,
  Grid,
} from "antd";
import React, { useState } from "react";
import { css } from "@emotion/css";
import { useDispatch, useSelector } from "react-redux";
import { setupEntityOptions } from "../DummyData";
import ArrowRightCircleIcon from "../../../../../../Theme/CustomIcons/Icons/Arrow/ArrowRightCircleIcon";
import ClockIcon from "../../../../../../Theme/CustomIcons/Icons/Time/ClockIcon";
import OverviewDrawerParent from "../Drawers/Overview/OverviewDrawerParent";
import DimensioneDrawerListingParent from "../Drawers/Dimensions/DimensioneDrawerListingParent";
import TickCircleIcon from "../../../../../../Theme/CustomIcons/Icons/Arrow/TickCircleIcon";
import ModulesDrawer from "../Drawers/Modules/ModulesDrawer";
import ThemeSelectionDrawer from "../Drawers/ThemeSelection/ThemeSelectionDrawer";
import { getAllDimensionsByEntity } from "../../../../../../store/action/admin/dimensionsEntityActions";

const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

const TAG_COLORS = {
  current: "green",
  active: "blue",
  draft: "orange",
  pending: "purple",
  completed: "green",
  default: "default",
};

const cardStyles = {
  card: (cardBorder) => css`
    display: flex;
    flex-direction: column;
    box-shadow: none !important;
    padding: 8px 8px !important;

    .ant-card-head {
      margin: 0 !important;
      padding: 8px 16px !important;
      border-bottom: ${cardBorder} !important;
    }
  `,
  circleIcon: (color, borderColor, isLightTheme) => css`
    background: ${color}29;
    border: 1px solid ${borderColor};
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: ${isLightTheme ? borderColor : "#B7EB8F"};
  `,
  numberCircle: (color) => css`
    color: ${color};
    background: ${color}29;
    border: 1px solid ${color};
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  `,
};

const SetupEntityCard = ({ entityData, entityId }) => {
  const themeDefault = useSelector((state) => state.themeDefault);
  const isLightTheme = themeDefault?.info?.themeColor === "default";
  const screens = useBreakpoint();
  const dispatch = useDispatch();

  // Placeholder: compute status/action if needed later
  const getTagColor = (status) => {
    const lower = (status || "").toLowerCase();
    return TAG_COLORS[lower] || TAG_COLORS.default;
  };
  const getAllDimensionList = useSelector((state) => state.getAllDimensionList);

  // Refresh dimensions whenever entity changes so UI reflects latest counts/status
  React.useEffect(() => {
    if (entityId) {
      dispatch(getAllDimensionsByEntity(1, 10, entityId));
    }
  }, [entityId, dispatch]);
  const getSectionStatusAndAction = (id) => {
    // Overview complete condition
    if (id === 1 && entityData?.entityId && entityData?.name) {
      return { status: "Completed", action: "Update" };
    }

    // Dimensions complete condition: when there are more than 1 dimensions
    if (id === 2) {
      const info = getAllDimensionList?.info;
      const list = Array.isArray(info?.data)
        ? info.data
        : Array.isArray(info)
        ? info
        : [];
      const count = list.length || 0;
      if (count > 1) {
        return { status: "Completed", action: "Update" };
      }
    }

    // Default for other options or pending states
    return { status: "Pending", action: "Setup" };
  };

  const [selectedDrawerId, setSelectedDrawerId] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const handleSetupClick = (optionId) => {
    setSelectedDrawerId(optionId);
    setIsDrawerOpen(true);
  };
  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  const componentMap = {
    OverviewDrawerParent: OverviewDrawerParent,
    DimensioneDrawerListingParent: DimensioneDrawerListingParent,
    ModulesDrawer: ModulesDrawer,
    ThemeSelectionDrawer: ThemeSelectionDrawer,
  };

  const ActiveComponent = (() => {
    const active = setupEntityOptions.find((o) => o.id === selectedDrawerId);
    if (!active) return null;
    const Comp = componentMap[active.component] || OverviewDrawerParent;
    return Comp;
  })();

  return (
    <>
      <Card
        title="Setup Entity"
        extra={<Tag color={getTagColor("current")}>current</Tag>}
        className={cardStyles.card(
          themeDefault?.info?.newConfig?.cardborder || "none"
        )}
      >
        <div
          style={{
            paddingRight: 8,
            overflowY: "auto",
            maxHeight: screens.xs ? "auto" : "calc(100vh - 200px)",
          }}
        >
          {setupEntityOptions.map((option, idx) => {
            const { status, action } = getSectionStatusAndAction(option.id);
            const isLast = idx === setupEntityOptions.length - 1;

            return (
              <Row
                key={option.id}
                gutter={[8, 8]}
                align="middle"
                wrap
                style={{
                  padding: "12px 8px",
                  borderBottom: isLast
                    ? "none"
                    : `${themeDefault?.info.newConfig?.cardborder}`,
                }}
              >
                <Col flex="none">
                  {action === "Update" ? (
                    <div
                      className={cardStyles.circleIcon(
                        themeDefault?.info?.newConfig?.themeColorSuccess ||
                          "#52c41a",
                        themeDefault?.info?.newConfig?.themeColorSuccess ||
                          "#52c41a",
                        isLightTheme
                      )}
                    >
                      {/* <ArrowRightCircleIcon /> */}
                      <TickCircleIcon />
                    </div>
                  ) : (
                    <div
                      className={cardStyles.numberCircle(
                        themeDefault?.info?.colorPrimary || "#1890ff"
                      )}
                    >
                      {option.id}
                    </div>
                  )}
                </Col>

                <Col flex="1" style={{ minWidth: 0 }}>
                  <Flex vertical style={{ paddingLeft: 8 }}>
                    <Title
                      style={{
                        margin: 0,
                        fontSize: screens.xs ? 14 : 16,
                        color: themeDefault?.info?.colorPrimary,
                      }}
                    >
                      {option.title}
                    </Title>
                    <Text
                      type="secondary"
                      style={{
                        fontSize: screens.xs ? 10 : 12,
                        marginBottom: 4,
                      }}
                    >
                      {option.description}
                    </Text>
                    <Text
                      type="secondary"
                      style={{ fontSize: screens.xs ? 10 : 12 }}
                    >
                      {status}
                    </Text>
                  </Flex>
                </Col>

                <Col flex="none">
                  <Space direction="vertical" align="end" size={8}>
                    <Flex align="center" gap={4}>
                      <ClockIcon size="small" />
                      <Text
                        type="secondary"
                        style={{ fontSize: screens.xs ? 10 : 12 }}
                      >
                        {option.duration}
                      </Text>
                    </Flex>
                    <Flex align="center" gap={2}>
                      <Button
                        type="link"
                        size="small"
                        onClick={() => handleSetupClick(option.id)}
                        style={{
                          border: "none",
                          color: themeDefault?.info?.colorPrimary,
                          padding: 0,
                          height: "auto",
                          lineHeight: 1,
                          fontSize: screens.xs ? 10 : 12,
                        }}
                      >
                        {action}
                        <ArrowRightCircleIcon size="small" />
                      </Button>
                    </Flex>
                  </Space>
                </Col>
              </Row>
            );
          })}
        </div>
      </Card>

      {ActiveComponent ? (
        <ActiveComponent
          open={isDrawerOpen}
          onClose={handleDrawerClose}
          entityId={entityId}
        />
      ) : null}
    </>
  );
};

export default SetupEntityCard;
